import React, { forwardRef } from 'react';
import Icon from './Icon';

/**
 * EGGS Select Component
 * Based on EGGS Design System specifications
 * 
 * @param {string} variant - default | error | success
 * @param {string} size - lg | md | sm
 * @param {string} label - Select label
 * @param {string} helperText - Helper text below select
 * @param {string} errorText - Error message (overrides helperText)
 * @param {boolean} required - Required field indicator
 * @param {boolean} disabled - Disabled state
 * @param {string} placeholder - Placeholder text
 * @param {Array} options - Array of option objects {value, label}
 * @param {string} className - Additional CSS classes
 * @param {object} props - Additional select props
 */
const Select = forwardRef(({
  variant = 'default',
  size = 'md',
  label,
  helperText,
  errorText,
  required = false,
  disabled = false,
  placeholder = 'Select an option...',
  options = [],
  className = '',
  ...props
}, ref) => {
  const isError = variant === 'error' || errorText;
  const isSuccess = variant === 'success';

  // Base classes for select container
  const containerClasses = [
    'relative',
    'w-full'
  ].join(' ');

  // Label classes
  const labelClasses = [
    'block',
    'font-burns',
    'font-normal',
    'text-eggs-dark',
    'mb-2',
    size === 'lg' ? 'text-base' : size === 'md' ? 'text-sm' : 'text-xs'
  ].join(' ');

  // Select wrapper classes
  const wrapperClasses = [
    'relative',
    'flex',
    'items-center'
  ].join(' ');

  // Base select classes
  const baseSelectClasses = [
    'w-full',
    'font-burns',
    'font-extralight',
    'bg-white',
    'border-2',
    'rounded-lg',
    'transition-all',
    'duration-200',
    'ease-in-out',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-1',
    'appearance-none',
    'cursor-pointer',
    disabled ? 'opacity-50 cursor-not-allowed' : ''
  ].join(' ');

  // Size-specific classes
  const sizeClasses = {
    lg: [
      'h-12',        // 48px
      'px-4',        // 16px
      'pr-12',       // Extra padding for chevron
      'text-lg'      // 18px
    ].join(' '),
    
    md: [
      'h-10',        // 40px
      'px-3',        // 12px
      'pr-10',       // Extra padding for chevron
      'text-base'    // 16px
    ].join(' '),
    
    sm: [
      'h-8',         // 32px
      'px-3',        // 12px
      'pr-9',        // Extra padding for chevron
      'text-sm'      // 14px
    ].join(' ')
  };

  // Variant-specific classes
  const variantClasses = {
    default: [
      'border-gray-300',
      'text-eggs-dark',
      'focus:border-eggs-yellow',
      'focus:ring-eggs-yellow/20'
    ].join(' '),
    
    error: [
      'border-error',
      'text-eggs-dark',
      'focus:border-error',
      'focus:ring-error/20'
    ].join(' '),
    
    success: [
      'border-success',
      'text-eggs-dark',
      'focus:border-success',
      'focus:ring-success/20'
    ].join(' ')
  };

  // Chevron positioning classes
  const chevronClasses = {
    lg: 'absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none',
    md: 'absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none',
    sm: 'absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none'
  };

  // Icon sizes based on select size
  const iconSizes = {
    lg: 20,
    md: 18,
    sm: 16
  };

  // Helper/error text classes
  const helperTextClasses = [
    'mt-2',
    'font-burns',
    'font-extralight',
    size === 'lg' ? 'text-sm' : 'text-xs',
    isError ? 'text-error' : isSuccess ? 'text-success' : 'text-gray-500'
  ].join(' ');

  const iconSize = iconSizes[size];
  const displayText = errorText || helperText;

  return (
    <div className={`${containerClasses} ${className}`}>
      {label && (
        <label className={labelClasses}>
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}
      
      <div className={wrapperClasses}>
        <select
          ref={ref}
          className={`${baseSelectClasses} ${sizeClasses[size]} ${variantClasses[isError ? 'error' : isSuccess ? 'success' : 'default']}`}
          disabled={disabled}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option, index) => (
            <option key={option.value || index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        
        <Icon 
          name="chevron-down" 
          size={iconSize}
          className={`${chevronClasses[size]} text-gray-500`}
        />
      </div>
      
      {displayText && (
        <p className={helperTextClasses}>
          {displayText}
        </p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

export default Select;